import React from 'react';
import UnitToggle from './UnitToggle';
import { NumberField } from './FormFields';
import { kgToLbs, lbsToKg, cmToFtIn, ftInToCm } from '../../lib/calculatorUtils';

/**
 * Height input switches between cm and ft/in.
 *
 * Props:
 *   unit: 'metric' | 'imperial'
 *   onUnitChange: (u) => void
 *   heightCm: number (canonical store)
 *   onHeightCmChange: (cm) => void
 *   error: string
 */
export const HeightInput = ({ unit, onUnitChange, heightCm, onHeightCmChange, error }) => {
  const handleCm = (val) => {
    const n = parseFloat(val);
    onHeightCmChange(isNaN(n) ? '' : n);
  };
  const { ft, inch } = heightCm ? cmToFtIn(heightCm) : { ft: '', inch: '' };

  const handleFt = (val) => {
    const f = parseFloat(val) || 0;
    const i = parseFloat(inch) || 0;
    onHeightCmChange(f || i ? +ftInToCm(f, i).toFixed(1) : '');
  };
  const handleInch = (val) => {
    const f = parseFloat(ft) || 0;
    const i = parseFloat(val) || 0;
    onHeightCmChange(f || i ? +ftInToCm(f, i).toFixed(1) : '');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="block text-sm font-medium text-slate-300">Height <span className="text-rose-400">*</span></span>
        <UnitToggle
          value={unit}
          onChange={onUnitChange}
          options={[{ value: 'metric', label: 'cm' }, { value: 'imperial', label: 'ft / in' }]}
          label="Height unit"
          testIdPrefix="height-unit"
        />
      </div>
      {unit === 'metric' ? (
        <NumberField
          id="height-cm"
          label=""
          value={heightCm}
          onChange={handleCm}
          placeholder="e.g. 170"
          suffix="cm"
          min={50}
          max={272}
          testId="height-cm-input"
          error={error}
        />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <NumberField id="height-ft" label="" value={ft} onChange={handleFt} placeholder="ft" suffix="ft" min={1} max={8} testId="height-ft-input" />
          <NumberField id="height-in" label="" value={inch} onChange={handleInch} placeholder="in" suffix="in" min={0} max={11} testId="height-in-input" />
          {error && <p className="text-rose-400 text-xs col-span-2">{error}</p>}
        </div>
      )}
    </div>
  );
};

/**
 * Weight input switches between kg and lbs.
 */
export const WeightInput = ({ unit, onUnitChange, weightKg, onWeightKgChange, error, label = 'Weight' }) => {
  const handle = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) {
      onWeightKgChange('');
      return;
    }
    onWeightKgChange(unit === 'metric' ? +n.toFixed(1) : +lbsToKg(n).toFixed(1));
  };
  const display = weightKg === '' ? '' : (unit === 'metric' ? weightKg : +kgToLbs(weightKg).toFixed(1));

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="block text-sm font-medium text-slate-300">{label} <span className="text-rose-400">*</span></span>
        <UnitToggle
          value={unit}
          onChange={onUnitChange}
          options={[{ value: 'metric', label: 'kg' }, { value: 'imperial', label: 'lbs' }]}
          label="Weight unit"
          testIdPrefix="weight-unit"
        />
      </div>
      <NumberField
        id="weight-input"
        label=""
        value={display}
        onChange={handle}
        placeholder={unit === 'metric' ? 'e.g. 70' : 'e.g. 154'}
        suffix={unit === 'metric' ? 'kg' : 'lbs'}
        min={20}
        max={500}
        testId="weight-input"
        error={error}
      />
    </div>
  );
};

/**
 * Generic length input (e.g. waist/neck/hip) with cm/in toggle that shares the body-measurement unit state.
 */
export const LengthInput = ({ id, label, unit, valueCm, onValueCmChange, error, testId, required = true }) => {
  const handle = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) {
      onValueCmChange('');
      return;
    }
    onValueCmChange(unit === 'metric' ? +n.toFixed(1) : +(n * 2.54).toFixed(1));
  };
  const display = valueCm === '' ? '' : (unit === 'metric' ? valueCm : +(valueCm / 2.54).toFixed(1));

  return (
    <NumberField
      id={id}
      label={label}
      value={display}
      onChange={handle}
      placeholder={unit === 'metric' ? 'cm' : 'in'}
      suffix={unit === 'metric' ? 'cm' : 'in'}
      min={10}
      max={300}
      testId={testId}
      error={error}
      required={required}
    />
  );
};
